import { mergeMilestoneClaims } from "@/event/domain/mergeEventMilestones";
import {
	createEmptyGrandPrizeState,
	mergeGrandPrizeFullState,
} from "@/event/domain/mergeGrandPrizeState";
import { requestEventMapAssetsLoad } from "@/event/integration/game/loadEventMapAssets";
import {
	clearEndedEventCollectables,
	clearEndedEventStarterBuildings,
	clearEndedEventUpgrades,
	clearEndedEventium,
	clearEventScopedResourcesWhenInactive,
	endedEventCleanupId,
} from "@/event/session/eventEndCleanup";
import { isPastEndDate } from "@/event/session/eventEndDate";
import { refreshInGameRewardsAfterEventEnd } from "@/event/session/eventEndRewardsRefresh";
import { scheduleEventEnd } from "@/event/session/eventEndSchedule";
import { persistEventCleanup } from "@/event/session/eventEndTeardownPersist";
import { tryShowIntroPopup } from "@/event/session/eventIntro";
import { clearStarterBuildingsOnEventStart } from "@/event/session/eventStartBuildingCleanup";
import type { EventAttributes, EventState } from "@/store/useEventStore.ts";
import type {
	ClaimablesCoordinates,
	EventMapCollectablesResult,
	EventOverviewResult,
	EventStarterBuildingGrant,
} from "@/types";
import { setImmutableStateProperty } from "@/utils/state";
import type { Ref } from "vue";

export type OverviewPatchDeps = {
	state: Ref<EventState>;
	initialAttributes: EventAttributes;
	endedCleanupOnceKeys: Set<string>;
	setCollectables: (collectables: EventMapCollectablesResult) => void;
	setTileClaimablesCoordinates: (
		coordinates: ClaimablesCoordinates[],
	) => void;
	closeEventPopup: () => void;
};

type PatchContext = {
	prevGen: number;
	prevId: string | null;
	allowOptimisticMerge: boolean;
	incomingGen: number | undefined;
	hasEventField: boolean;
};

function buildPatchContext(
	state: EventState,
	value: Partial<EventOverviewResult>,
): PatchContext {
	const prevGen = state.eventGeneration;
	const prevId = state.id;
	const hasEventField = Object.prototype.hasOwnProperty.call(value, "event");
	const incomingGen =
		typeof value.eventGeneration === "number" &&
		Number.isFinite(value.eventGeneration)
			? value.eventGeneration
			: undefined;

	const allowOptimisticMerge =
		prevId !== null &&
		(incomingGen === undefined || incomingGen === prevGen) &&
		(!hasEventField ||
			(value.event !== null &&
				value.event !== undefined &&
				value.event.id === prevId));

	return {
		prevGen,
		prevId,
		allowOptimisticMerge,
		incomingGen,
		hasEventField,
	};
}

/** Ignore partial `event: null` until admin calendar end, generation bump, or no local event. */
function shouldApplyNullOverview(
	state: EventState,
	incomingGen: number | undefined,
): boolean {
	if (state.id === null) return true;

	if (isPastEndDate(state.endDate)) return true;

	return incomingGen !== undefined && incomingGen > state.eventGeneration;
}

function resetEndedState(deps: OverviewPatchDeps): void {
	const { state, initialAttributes } = deps;

	setImmutableStateProperty(state, "id", null);
	setImmutableStateProperty(state, "isServerActive", false);
	setImmutableStateProperty(state, "startDate", null);
	setImmutableStateProperty(state, "endDate", null);
	setImmutableStateProperty(state, "attributes", { ...initialAttributes });
	setImmutableStateProperty(state, "leaderboards", null);
	setImmutableStateProperty(state, "leaderboardLoadTimestamp", null);
	setImmutableStateProperty(state, "claimedMilestoneIds", null);
	setImmutableStateProperty(state, "collectedByType", {});
	setImmutableStateProperty(state, "initBuildingsClaimed", false);
	setImmutableStateProperty(state, "introDismissed", false);
	setImmutableStateProperty(state, "starterMapCleaned", false);
	setImmutableStateProperty(state, "starterBuildingGrants", []);
	setImmutableStateProperty(state, "eventPassPurchased", false);
	setImmutableStateProperty(state, "mapClaimablesCoordinates", []);
	setImmutableStateProperty(state, "isIntroPopupActive", false);
	setImmutableStateProperty(state, "grandPrizeState", null);
}

function teardownIfEnded(deps: OverviewPatchDeps, nowMs?: number): void {
	const { state } = deps;
	if (!isPastEndDate(state.value.endDate, nowMs)) return;

	deps.closeEventPopup();
	setImmutableStateProperty(state, "isIntroPopupActive", false);

	const snapshot = {
		eventId: state.value.id,
		eventType: state.value.attributes?.type ?? undefined,
		startBuildingClaims: state.value.starterBuildingGrants,
		endDate: state.value.endDate ?? undefined,
		nowMs,
	};

	const cleanupId = endedEventCleanupId(snapshot);
	const eventiumCleared = clearEndedEventium(
		snapshot,
		deps.endedCleanupOnceKeys,
	);
	const starterBuildingsCleared = clearEndedEventStarterBuildings(
		{
			eventId: snapshot.eventId,
			eventType: snapshot.eventType,
			startBuildingClaims: snapshot.startBuildingClaims,
		},
		deps.endedCleanupOnceKeys,
	);
	clearEndedEventCollectables();
	clearEndedEventUpgrades();

	if (cleanupId !== null || eventiumCleared || starterBuildingsCleared) {
		void persistEventCleanup();
	}

	refreshInGameRewardsAfterEventEnd();

	if (state.value.id !== null) {
		resetEndedState(deps);
	}
}

function applyActiveEventOverviewPatch(
	deps: OverviewPatchDeps,
	value: Partial<EventOverviewResult> & {
		event: NonNullable<EventOverviewResult["event"]>;
	},
): void {
	const { state, initialAttributes } = deps;

	if (
		state.value.id === value.event.id &&
		isPastEndDate(state.value.endDate) &&
		value.event.active
	) {
		// Admin calendar ended — ignore stale BE active:true until a new event id arrives.
		return;
	}

	setImmutableStateProperty(state, "id", value.event.id);
	setImmutableStateProperty(state, "isServerActive", value.event.active);
	setImmutableStateProperty(
		state,
		"startDate",
		new Date(value.event.start_date),
	);
	setImmutableStateProperty(state, "endDate", new Date(value.event.end_date));

	if (value.event.attributes) {
		const mergedAttrs = {
			...initialAttributes,
			...value.event.attributes,
		};
		setImmutableStateProperty(state, "attributes", mergedAttrs);
	}
}

/** BE `event: null` syncs local ended state only — map/inventory teardown uses admin calendar. */
function applyNullEventOverviewPatch(
	deps: OverviewPatchDeps,
	incomingGen: number | undefined,
): void {
	teardownIfEnded(deps);
	const reason =
		incomingGen !== undefined
			? `gen:${incomingGen}`
			: `id:${deps.state.value.id ?? "none"}`;
	const scopedCleared = clearEventScopedResourcesWhenInactive(
		deps.endedCleanupOnceKeys,
		reason,
	);
	if (scopedCleared) {
		clearEndedEventCollectables();
		clearEndedEventUpgrades();
	}

	if (scopedCleared) {
		void persistEventCleanup();
	}

	deps.closeEventPopup();
	setImmutableStateProperty(deps.state, "isIntroPopupActive", false);
	resetEndedState(deps);
}

function applyMilestonesOverviewPatch(
	deps: OverviewPatchDeps,
	value: Partial<EventOverviewResult>,
	ctx: PatchContext,
): void {
	if (!("milestones" in value) || value.milestones === undefined) return;

	let nextMilestones = value.milestones;
	if (
		ctx.allowOptimisticMerge &&
		nextMilestones !== null &&
		deps.state.value.claimedMilestoneIds
	) {
		nextMilestones = mergeMilestoneClaims(
			deps.state.value.claimedMilestoneIds,
			nextMilestones,
		);
	}
	setImmutableStateProperty(
		deps.state,
		"claimedMilestoneIds",
		nextMilestones,
	);
}

function applyGrandPrizeOverviewPatch(
	deps: OverviewPatchDeps,
	value: Partial<EventOverviewResult>,
	ctx: PatchContext,
): void {
	if (!("grandPrizeState" in value) || value.grandPrizeState === undefined) {
		return;
	}

	let nextGrandPrizeState =
		value.grandPrizeState ??
		(value.event?.attributes?.grand_prizes
			? createEmptyGrandPrizeState()
			: null);
	if (
		ctx.allowOptimisticMerge &&
		nextGrandPrizeState !== null &&
		deps.state.value.grandPrizeState
	) {
		nextGrandPrizeState = mergeGrandPrizeFullState(
			deps.state.value.grandPrizeState,
			nextGrandPrizeState,
		);
	}
	setImmutableStateProperty(
		deps.state,
		"grandPrizeState",
		nextGrandPrizeState,
	);
}

function applyCollectablesOverviewPatch(
	deps: OverviewPatchDeps,
	value: Partial<EventOverviewResult>,
	ctx: PatchContext,
): void {
	if (!("collectables" in value)) return;
	if (ctx.allowOptimisticMerge) return;

	if (value.collectables) {
		deps.setCollectables(value.collectables);
		return;
	}

	if (value.collectables === null) {
		setImmutableStateProperty(deps.state, "collectedByType", {});
		deps.setTileClaimablesCoordinates([]);
		clearEndedEventCollectables();
	}
}

function applyOverviewFlagsPatch(
	deps: OverviewPatchDeps,
	value: Partial<EventOverviewResult>,
): void {
	if ("initBuildingsClaimed" in value) {
		setImmutableStateProperty(
			deps.state,
			"initBuildingsClaimed",
			!!value.initBuildingsClaimed,
		);
	}

	if ("introDismissed" in value) {
		setImmutableStateProperty(
			deps.state,
			"introDismissed",
			!!value.introDismissed,
		);
	}

	if ("starterMapCleaned" in value) {
		setImmutableStateProperty(
			deps.state,
			"starterMapCleaned",
			!!value.starterMapCleaned,
		);
	}

	if ("eventPassPurchased" in value) {
		setImmutableStateProperty(
			deps.state,
			"eventPassPurchased",
			!!value.eventPassPurchased,
		);
	}

	if (
		"starterBuildingGrants" in value &&
		value.starterBuildingGrants !== undefined
	) {
		setImmutableStateProperty(
			deps.state,
			"starterBuildingGrants",
			value.starterBuildingGrants as EventStarterBuildingGrant[],
		);
	}
}

function applyStarterMapCleanup(
	deps: OverviewPatchDeps,
	value: Partial<EventOverviewResult>,
): void {
	if (!value.event || value.starterMapCleaned !== false) return;

	const buildingsCleared = clearStarterBuildingsOnEventStart(
		{
			eventId: value.event.id,
			startBuildingClaims: value.event.attributes?.start_building_claims,
			overviewGrants: value.starterBuildingGrants,
		},
		deps.endedCleanupOnceKeys,
	);
	if (!buildingsCleared) return;

	setImmutableStateProperty(deps.state, "starterMapCleaned", true);
	void persistEventCleanup();
}

function applyIntroPopup(value: Partial<EventOverviewResult>): void {
	if (value.event && value.introDismissed === false) {
		tryShowIntroPopup();
	}
}

function applyEventGenerationOverviewPatch(
	deps: OverviewPatchDeps,
	value: Partial<EventOverviewResult>,
): void {
	if (
		!("eventGeneration" in value) ||
		typeof value.eventGeneration !== "number" ||
		!Number.isFinite(value.eventGeneration)
	) {
		return;
	}

	setImmutableStateProperty(
		deps.state,
		"eventGeneration",
		Math.max(deps.state.value.eventGeneration, value.eventGeneration),
	);
}

export function applyOverviewPatch(
	deps: OverviewPatchDeps,
	value?: Partial<EventOverviewResult>,
): void {
	if (!value) return;

	const ctx = buildPatchContext(deps.state.value, value);

	if (ctx.hasEventField && value.event) {
		applyActiveEventOverviewPatch(deps, {
			...value,
			event: value.event,
		});
	} else if (
		ctx.hasEventField &&
		(value.event === null || value.event === undefined)
	) {
		if (!shouldApplyNullOverview(deps.state.value, ctx.incomingGen)) {
			return;
		}
		applyNullEventOverviewPatch(deps, ctx.incomingGen);
	}

	applyMilestonesOverviewPatch(deps, value, ctx);
	applyGrandPrizeOverviewPatch(deps, value, ctx);
	applyCollectablesOverviewPatch(deps, value, ctx);
	applyOverviewFlagsPatch(deps, value);
	applyStarterMapCleanup(deps, value);
	applyIntroPopup(value);
	applyEventGenerationOverviewPatch(deps, value);

	scheduleEventEnd({
		eventId: deps.state.value.id,
		endDate: deps.state.value.endDate,
	});

	if (deps.state.value.isServerActive && deps.state.value.attributes?.type) {
		requestEventMapAssetsLoad();
	}
}

export function catchUpEventEnd(deps: OverviewPatchDeps): void {
	teardownIfEnded(deps);
}
